import apiClient from "../http/apiClient";
import { CASH_ENDPOINTS } from "../http/endpoints";

const registerUrl = (id) => `${CASH_ENDPOINTS.cashRegisters}${id}/`;
const closeSessionUrl = (id) => `${CASH_ENDPOINTS.cashSessionsBase}${id}/close/`;
const receiveSessionUrl = (id) =>
  `${CASH_ENDPOINTS.cashSessionsBase}${id}/${CASH_ENDPOINTS.cashSessionReceiveSuffix || "receive/"}`;
const sessionUrl = (id) => `${CASH_ENDPOINTS.cashSessionsBase}${id}/`;
const sessionReconcileUrl = (id) => `${CASH_ENDPOINTS.cashSessionsBase}${id}/reconcile/`;
const voidMovementUrl = (id) => `${CASH_ENDPOINTS.cashMovements}${id}/void/`;
const relevantSessionCache = new Map();
const relevantSessionInflight = new Map();
const RELEVANT_SESSION_TTL_MS = 1500;

const buildRelevantSessionKey = (params = {}) =>
  JSON.stringify({
    branch_id: params?.branch_id ?? null,
    cash_register_id: params?.cash_register_id ?? null,
    date: params?.date ?? null,
    include_movements: Boolean(params?.include_movements),
  });

export async function listRegisters(params = {}) {
  const response = await apiClient.get(CASH_ENDPOINTS.cashRegisters, { params });
  return response.data;
}

export async function createRegister(payload = {}) {
  const response = await apiClient.post(CASH_ENDPOINTS.cashRegisters, payload);
  return response.data;
}

export async function updateRegister(id, payload = {}) {
  const hasId = id !== null && id !== undefined;
  const target = hasId ? registerUrl(id) : CASH_ENDPOINTS.cashRegisters;
  const response = await apiClient.patch(target, payload);
  return response.data;
}

export async function currentSession(params = {}) {
  const cacheKey = buildRelevantSessionKey(params);
  const now = Date.now();
  const cached = relevantSessionCache.get(cacheKey);
  if (cached && now - cached.at < RELEVANT_SESSION_TTL_MS) {
    return cached.data;
  }

  if (relevantSessionInflight.has(cacheKey)) {
    return relevantSessionInflight.get(cacheKey);
  }

  const requestPromise = apiClient
    .get(CASH_ENDPOINTS.cashSessionsRelevant || CASH_ENDPOINTS.cashSessionsCurrent, { params })
    .then((response) => {
      relevantSessionCache.set(cacheKey, { at: Date.now(), data: response.data });
      relevantSessionInflight.delete(cacheKey);
      return response.data;
    })
    .catch((error) => {
      relevantSessionInflight.delete(cacheKey);
      throw error;
    });

  relevantSessionInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function relevantSession(params = {}) {
  return currentSession(params);
}

export async function openSessionNotice(params = {}) {
  const response = await apiClient.get(CASH_ENDPOINTS.cashSessionsOpenNotice, { params });
  return response.data;
}

export async function openSession(payload = {}) {
  const response = await apiClient.post(CASH_ENDPOINTS.cashSessionsOpen, payload);
  relevantSessionCache.clear();
  return response.data;
}

export async function closeSession(id, payload = {}) {
  const response = await apiClient.post(closeSessionUrl(id), payload);
  relevantSessionCache.clear();
  return response.data;
}

export async function receiveSession(id, payload = {}) {
  const response = await apiClient.post(receiveSessionUrl(id), payload);
  relevantSessionCache.clear();
  return response.data;
}

export async function listSessions(params = {}) {
  const response = await apiClient.get(CASH_ENDPOINTS.cashSessionsBase, { params });
  return response.data;
}

export async function getSession(id, params = {}) {
  const response = await apiClient.get(sessionUrl(id), { params });
  return response.data;
}

export async function reconcileSession(id, payload = {}) {
  const response = await apiClient.post(sessionReconcileUrl(id), payload);
  relevantSessionCache.clear();
  return response.data;
}

export async function createMovement(payload = {}) {
  const response = await apiClient.post(CASH_ENDPOINTS.cashMovements, payload);
  relevantSessionCache.clear();
  return response.data;
}

export async function listMovements(params = {}) {
  const response = await apiClient.get(CASH_ENDPOINTS.cashMovements, { params });
  return response.data;
}

export async function voidMovement(id, payload = {}) {
  const response = await apiClient.post(voidMovementUrl(id), payload);
  relevantSessionCache.clear();
  return response.data;
}

// Backward-compatible aliases
export async function listCashSessions(params = {}) {
  return listSessions(params);
}

export async function getCashSession() {
  return currentSession();
}

export async function openCashSession(payload = {}) {
  return openSession(payload);
}

export async function closeCashSession(id, payload = {}) {
  return closeSession(id, payload);
}

export async function listCashRegisters(params = {}) {
  return listRegisters(params);
}

export const cashService = {
  listRegisters,
  createRegister,
  updateRegister,
  currentSession,
  relevantSession,
  openSessionNotice,
  openSession,
  closeSession,
  receiveSession,
  listSessions,
  getSession,
  reconcileSession,
  createMovement,
  listMovements,
  voidMovement,
  listCashSessions,
  getCashSession,
  openCashSession,
  closeCashSession,
  listCashRegisters,
};

export default cashService;
